import React from "react";
import Seo from "../components/Seo";

const PROMISE = [
  {
    title: "Every bag authenticated before it ships",
    body: (
      <>
        No piece leaves our studio without being inspected against the designer's
        construction, hardware, stitching, and material benchmarks. We verify serial numbers,
        date codes, heat stamps, hardware weight, and interior lining details against a
        reference database maintained in-house.
      </>
    ),
  },
  {
    title: "Certificate of authenticity",
    body: (
      <>
        Every order ships with a signed certificate of authenticity bearing a unique
        verification code tied to your order number. Keep this card with your bag — it protects
        resale value and confirms provenance.
      </>
    ),
  },
  {
    title: "100% money-back guarantee",
    body: (
      <>
        If a bag we sold is ever deemed inauthentic by a recognised independent authentication
        service (Entrupy, Real Authentication, or CoutureUSA), we will <strong>refund 100%</strong> of
        the purchase price <strong>plus the authentication fee</strong> within 5 business days of
        written confirmation. No restocking fees. No questions.
      </>
    ),
  },
];

const PROCESS = [
  {
    title: "1. Sourcing",
    body: (
      <>
        We only source from estate sales, reputable private sellers with documented
        provenance, and verified reseller partners. No street markets. No anonymous online
        drops.
      </>
    ),
  },
  {
    title: "2. In-studio inspection",
    body: (
      <>
        Each piece is evaluated under magnification by our lead authenticator — 20+
        benchmarks per bag covering stitching density, hardware stampings, zipper pulls,
        interior lining, serial placement, and weight.
      </>
    ),
  },
  {
    title: "3. Documentation",
    body: (
      <>
        Photos of the key authenticity markers are captured and stored with the order record.
        If you ever need to re-verify your bag, email us with your order number and we'll
        provide the documentation on file.
      </>
    ),
  },
  {
    title: "4. Dispatch",
    body: (
      <>
        Only after all checks pass does the bag ship — individually wrapped, in its dust bag,
        with certificate card and care instructions.
      </>
    ),
  },
];

export default function Authenticity() {
  return (
    <div className="pt-32 pb-24" data-testid="authenticity-page">
      <Seo
        title="Authenticity Guarantee"
        description="Every Elegant Affairs bag is inspected against 20+ benchmarks and backed by a 100% money-back authenticity guarantee."
      />
      <div className="max-w-[860px] mx-auto px-6 md:px-12">
        <p className="eyebrow">Guarantee</p>
        <h1 className="font-display text-5xl md:text-7xl mt-4 leading-[0.95] tracking-tighter">
          100% Authentic.
          <br />
          <em className="font-light">Every time.</em>
        </h1>
        <p className="text-[var(--mn-ink-2)] mt-6 leading-relaxed text-lg">
          The one thing we refuse to compromise. Every piece is independently verified
          before it reaches you — and backed by a money-back guarantee in writing.
        </p>
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-4">
          Last updated: April 2026
        </p>

        <div className="mt-16">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Our promise</h2>
          <div className="mt-10 space-y-12">
            {PROMISE.map((s) => (
              <section key={s.title}>
                <h3 className="font-display text-xl md:text-2xl tracking-tight">{s.title}</h3>
                <div className="text-[var(--mn-ink-2)] leading-relaxed mt-3 text-base">{s.body}</div>
              </section>
            ))}
          </div>
        </div>

        <div className="mt-24">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Our process</h2>
          <div className="mt-10 space-y-12">
            {PROCESS.map((s) => (
              <section key={s.title}>
                <h3 className="font-display text-xl md:text-2xl tracking-tight">{s.title}</h3>
                <div className="text-[var(--mn-ink-2)] leading-relaxed mt-3 text-base">{s.body}</div>
              </section>
            ))}
          </div>
        </div>

        <div className="mt-20 p-6 border-l-2 border-[var(--mn-accent)] bg-neutral-50/60">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--mn-muted)] mb-2">
            Independent verification
          </p>
          <p className="text-base text-[var(--mn-ink)] leading-relaxed">
            Recommended authentication services for a second opinion: <strong>Entrupy</strong>,{" "}
            <strong>Real Authentication</strong>, and <strong>CoutureUSA</strong>. We honour results
            from any of these three. Keep your receipts — we'll reimburse the authentication fee
            if a bag we sold is determined inauthentic.
          </p>
        </div>

        <div className="mt-10 p-6 border-l-2 border-[var(--mn-accent)] bg-neutral-50/60">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--mn-muted)] mb-2">
            Questions?
          </p>
          <p className="text-base text-[var(--mn-ink)]">
            Email{" "}
            <a href="mailto:concierge@elegantaffairs.shop" className="underline">
              concierge@elegantaffairs.shop
            </a>{" "}
            — we reply within 24 hours.
          </p>
        </div>
      </div>
    </div>
  );
}
